import { Space } from 'antd';
import classNames from 'classnames';
import React from 'react';
import { Form, useField } from 'react-final-form';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';

import { useSelector } from 'hooks/useSelector';
import { forgotPasswordSubmit } from 'modules/auth/actions';
import { getForgotPasswordSubmitIsLoading } from 'modules/auth/selectors';
import { getCustomName } from 'modules/users/selectors/currentUser';
import { Button, Card, Loader } from 'ui/base';

import { cardStyle, resetPasswordCardStyle } from './UI';
import Password from '../../../configuration/dealer/users/detail/components/fields/Password';
import PasswordConfirm from '../../../configuration/dealer/users/detail/components/fields/PasswordConfirm';
import { PasswordErrors } from '../../../configuration/dealer/users/detail/components/fields/PasswordErrors';

const ResetPasswordForm = ({ handleSubmit, hasValidationErrors }) => {
  const navigate = useNavigate();

  const isLoading = useSelector(getForgotPasswordSubmitIsLoading);

  const password = useField('password', { subscription: { value: true } }).input.value;

  const handleCancel = React.useCallback(() => navigate('/'), [navigate]);

  return (
    <Card
      variant="borderless"
      data-cy="reset-password-card"
      className={classNames(cardStyle, resetPasswordCardStyle)}
      title={<FormattedMessage id="resetPassword" defaultMessage="Reset password" />}
    >
      {isLoading && <Loader spinning className="d-block text-center m-4" />}
      {!isLoading && (
        <div className="px-4">
          <Password label={<FormattedMessage id="password" defaultMessage="Password" />} hideErrorMessage shortError />
          <PasswordErrors password={password} className="mt-2" />
          <PasswordConfirm label={<FormattedMessage id="confirmPassword" defaultMessage="Password confirmation" />} />
          <div className="border-top mt-4 py-4 text-right">
            <Space>
              <Button onClick={handleCancel} data-cy="cancelReset">
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              </Button>
              <Button type="primary" loading={isLoading} data-cy="submitReset" onClick={handleSubmit} disabled={hasValidationErrors}>
                <FormattedMessage id="ok" defaultMessage="Ok" />
              </Button>
            </Space>
          </div>
        </div>
      )}
    </Card>
  );
};

const ResetPassword = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const customName = useSelector(getCustomName);

  const handleSubmit = data => {
    const { password } = data;
    dispatch(forgotPasswordSubmit({
      newPassword: password,
      username: searchParams.get('username'),
      confirmationCode: searchParams.get('code'),
    }));
  };

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage(
            {
              id: 'app_tab.reset_password',
              defaultMessage: '{name} · Reset password',
            },
            { name: customName }
          )}
        </title>
      </Helmet>
      <Form onSubmit={handleSubmit} component={ResetPasswordForm} subscription={{ hasValidationErrors: true }} />
    </>
  );
};

export default ResetPassword;
